import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { useTranslation } from '../../../i18n';

import { SlideOver, SlideOverFooter } from '../../../components/Shared/SlideOver';
import { FormField } from '../../../components/Shared/FormField';
import { TextArea } from '../../../components/Shared/TextArea';
import { Button } from '../../../components/Shared/Button';
import { EmptyState } from '../../../components/Shared/EmptyState';
import SearchSelect from '../../../components/Shared/SearchSelect';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/Shared/Tabs';
import { ScheduleRuleInlineEditor } from '../../Tasks/components/ScheduleRuleInlineEditor';
import { TaskForm } from '../../Tasks/components/TaskForm';

import { useOfferStore } from '../../../store/useOfferStore';
import { useServiceStore } from '../../../store/useServiceStore';
import { selectObjectTasks, useTaskStore } from '../../../store/useTaskStore';
import { useObjectStore } from '../../../store/useObjectStore';
import { useScheduleRuleStore } from '../../../store/useScheduleRuleStore';
import {
  BillingKinds,
  billingFromTaskSchedule,
  isBillingRuleComplete,
  type BillingKind,
  type BillingRule,
} from '../../../utils/billingKind';
import type {
  FacilityFlowCoreModulesCustomersDtosObjectDto as ObjectDto,
  FacilityFlowCoreModulesOffersDtosOfferPositionDto as PositionDto,
} from '../../../api/generated/models';

import { BillingRuleFields } from './BillingRuleFields';
import { TaskPickerTab } from './TaskPickerTab';
import { toEditForm, type EditForm } from '../logic/positionEditForm';

interface PositionSlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  offerId: string;
  objectId?: string | null;
  /** Edit mode when set. */
  position?: PositionDto | null;
}

/** One position to create from a task, edited in the "Abrechnung" step. */
interface PositionDraft {
  taskId: string;
  taskName: string;
  title: string;
  netAmount: string;
  kind: BillingKind | null;
  rule: BillingRule;
}

type Tab = 'tasks' | 'newTask';
type Step = 'choose' | 'billing';

const isValidAmount = (value: string) => value !== '' && !Number.isNaN(parseFloat(value)) && parseFloat(value) >= 0;

/**
 * Add / edit an offer position (FAFD-27). Every new position belongs to a task of the offer's
 * object: an existing one ("Aus Aufgaben") or one created with the regular task form ("Neue
 * Aufgabe", all fields incl. schedule and checklist). The second step sets per task how the
 * invoice bills it, proposed from the task's schedule. Editing changes every field, the billing
 * rule, links a legacy position to a task and edits the linked task's schedule.
 */
export function PositionSlideOver({ isOpen, onClose, offerId, objectId, position }: PositionSlideOverProps) {
  const { t } = useTranslation(['offers', 'common', 'tasks']);
  const isEdit = !!position;
  const { saving, updatePosition, addPositionsFromTasks } = useOfferStore();
  const { services, fetchServices } = useServiceStore();
  const fetchObjectTasks = useTaskStore((s) => s.fetchObjectTasks);
  const tasks = useTaskStore(selectObjectTasks(objectId ?? undefined, true));
  const { getObjectById } = useObjectStore();

  const [tab, setTab] = useState<Tab>('tasks');
  const [step, setStep] = useState<Step>('choose');
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [drafts, setDrafts] = useState<PositionDraft[]>([]);
  const [object, setObject] = useState<ObjectDto | null>(null);
  // why: on a quarterly object, quarterly positions follow its quarters (FAFD-27).
  const [preparing, setPreparing] = useState(false);
  const [touched, setTouched] = useState(false);
  const [form, setForm] = useState<EditForm | null>(null);
  // why: TaskForm calls onClose right after onCreated; that close must not end this dialog.
  const taskJustCreated = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    setTab('tasks');
    setStep('choose');
    setSelectedTaskIds([]);
    setDrafts([]);
    setTouched(false);
    setForm(position ? toEditForm(position) : null);
  }, [isOpen, position]);

  useEffect(() => {
    if (!isOpen) return;
    void fetchServices();
    if (objectId) {
      void fetchObjectTasks(objectId, { activeOnly: true });
      void getObjectById(objectId).then((o) => setObject(o as ObjectDto | null));
    }
  }, [isOpen, objectId, fetchServices, fetchObjectTasks, getObjectById]);

  const priceOf = (serviceId?: string) => services.find((s) => s.id === serviceId)?.basePrice;

  /** Draft for a task, billing proposed from its schedule — none when its rules fail to load. */
  const draftFor = async (task: { taskId: string; name: string; serviceId: string }): Promise<PositionDraft> => {
    const rules = await useScheduleRuleStore.getState().peekRules(task.taskId);
    const proposal = rules ? billingFromTaskSchedule(rules) : { kind: BillingKinds.Unknown, rule: {} };
    const price = priceOf(task.serviceId);
    return {
      taskId: task.taskId,
      taskName: task.name,
      title: task.name,
      netAmount: price != null ? String(price) : '',
      kind: proposal.kind,
      rule: proposal.rule,
    };
  };

  const goToBilling = async (chosen: { taskId: string; name: string; serviceId: string }[]) => {
    setPreparing(true);
    try {
      const next: PositionDraft[] = [];
      for (const task of chosen) next.push(await draftFor(task));
      setDrafts(next);
      setTouched(false);
      setStep('billing');
    } finally {
      setPreparing(false);
    }
  };

  const updateDraft = (taskId: string, patch: Partial<PositionDraft>) =>
    setDrafts((list) => list.map((d) => (d.taskId === taskId ? { ...d, ...patch } : d)));

  const draftsValid = drafts.every(
    (d) => d.title.trim() && isValidAmount(d.netAmount) && isBillingRuleComplete(d.kind, d.rule),
  );

  const saveDrafts = async () => {
    setTouched(true);
    if (!draftsValid) return;
    try {
      await addPositionsFromTasks(offerId, drafts.map((d) => ({
        taskDefinitionId: d.taskId,
        title: d.title.trim(),
        netAmount: parseFloat(d.netAmount),
        recurrenceKind: d.kind ?? undefined,
        billingRule: d.rule,
      })));
      onClose();
    } catch {
      // showApiError already toasted; keep the dialog open
    }
  };

  const editValid = !!form && !!form.title.trim() && isValidAmount(form.netAmount)
    && isBillingRuleComplete(form.kind, form.rule);

  const saveEdit = async () => {
    setTouched(true);
    if (!form || !position?.id || !editValid) return;
    try {
      await updatePosition(offerId, position.id, {
        title: form.title.trim(),
        description: form.description,
        // why: a linked position's interval text follows its task's schedule server-side.
        interval: form.taskId ? null : form.interval,
        netAmount: parseFloat(form.netAmount),
        instructions: form.instructions,
        recurrenceKind: form.kind ?? undefined,
        billingRule: form.rule,
        taskDefinitionId: form.taskId || null,
      });
      onClose();
    } catch {
      // showApiError already toasted; keep the dialog open
    }
  };

  const taskOptions = useMemo(
    () => tasks.map((task) => ({ id: task.taskId, label: task.name, description: task.serviceName })),
    [tasks],
  );

  /* ---------------------------------- Edit ---------------------------------- */
  const editBody = form && (
    <div className="space-y-4">
      <FormField
        label={t('offers:positions.fields.title')}
        required
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        error={touched && !form.title.trim() ? t('offers:positions.validation.titleRequired') : undefined}
      />
      <FormField
        label={t('offers:positions.fields.netAmount')}
        type="number"
        min={0}
        step="0.01"
        required
        value={form.netAmount}
        onChange={(e) => setForm({ ...form, netAmount: e.target.value })}
        error={touched && !isValidAmount(form.netAmount) ? t('offers:positions.validation.netAmountRequired') : undefined}
      />
      <BillingRuleFields
        kind={form.kind}
        rule={form.rule}
        onChange={(kind, rule) => setForm({ ...form, kind, rule })}
        error={touched && !isBillingRuleComplete(form.kind, form.rule) ? t('offers:billing.required') : undefined}
      />

      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[12px] font-medium text-slate-700">{t('offers:positions.fields.task')}</div>
          {form.taskId && (
            <Link
              to={`/tasks/${form.taskId}`}
              className="inline-flex items-center gap-1 text-[12px] text-[#00A871] hover:underline"
            >
              {t('offers:positions.openTask')}
              <ExternalLink className="w-3.5 h-3.5" strokeWidth={2} />
            </Link>
          )}
        </div>
        {position?.taskDefinitionId ? (
          <>
            <ScheduleRuleInlineEditor taskId={position.taskDefinitionId} />
            <p className="text-[11px] text-slate-500">{t('offers:positions.scheduleHint')}</p>
          </>
        ) : (
          <>
            {form.kind === BillingKinds.ByEffort ? (
              <p className="text-[11px] text-red-700">{t('offers:positions.noTaskEffortHint')}</p>
            ) : (
              <p className="text-[11px] text-amber-700">{t('offers:positions.noTaskHint')}</p>
            )}
            <SearchSelect
              options={taskOptions}
              value={form.taskId}
              onChange={(taskId) => setForm({ ...form, taskId })}
              placeholder={t('offers:positions.placeholders.linkTask')}
              noResultsText={t('offers:positions.taskList.empty')}
              inlineDropdown
            />
            {!form.taskId && (
              <FormField
                label={t('offers:positions.fields.interval')}
                value={form.interval}
                onChange={(e) => setForm({ ...form, interval: e.target.value })}
                placeholder={t('offers:positions.placeholders.interval')}
              />
            )}
          </>
        )}
      </div>

      <TextArea
        label={t('offers:positions.fields.description')}
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        rows={3}
      />
      <TextArea
        label={t('offers:positions.fields.instructions')}
        value={form.instructions}
        onChange={(e) => setForm({ ...form, instructions: e.target.value })}
        rows={4}
      />
    </div>
  );

  /* ----------------------------- Billing step ----------------------------- */
  const billingBody = (
    <div className="space-y-4">
      <p className="text-[12px] text-slate-500">{t('offers:positions.billingStepHint')}</p>
      {drafts.map((d) => (
        <div key={d.taskId} className="rounded-xl border border-slate-200 p-4 space-y-3">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{d.taskName}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField
              label={t('offers:positions.fields.title')}
              required
              value={d.title}
              onChange={(e) => updateDraft(d.taskId, { title: e.target.value })}
              error={touched && !d.title.trim() ? t('offers:positions.validation.titleRequired') : undefined}
            />
            <FormField
              label={t('offers:positions.fields.netAmount')}
              type="number"
              min={0}
              step="0.01"
              required
              value={d.netAmount}
              onChange={(e) => updateDraft(d.taskId, { netAmount: e.target.value })}
              error={touched && !isValidAmount(d.netAmount) ? t('offers:positions.validation.netAmountRequired') : undefined}
            />
          </div>
          <BillingRuleFields
            kind={d.kind}
            rule={d.rule}
            onChange={(kind, rule) => updateDraft(d.taskId, { kind, rule })}
            error={touched && !isBillingRuleComplete(d.kind, d.rule) ? t('offers:billing.required') : undefined}
          />
        </div>
      ))}
    </div>
  );

  /* ----------------------------- Choose step ------------------------------ */
  // why: every position comes from a task of the offer's object (FAFD-27) — an offer without
  // object (older offers) first needs one.
  const chooseBody = !objectId ? (
    <EmptyState
      title={t('offers:positions.objectRequired.title')}
      description={t('offers:positions.objectRequired.hint')}
    />
  ) : (
    <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
      <TabsList>
        <TabsTrigger value="tasks">{t('offers:positions.tabs.fromTasks')}</TabsTrigger>
        <TabsTrigger value="newTask">{t('offers:positions.tabs.newTask')}</TabsTrigger>
      </TabsList>
      <TabsContent value="tasks">
        <div className="pt-4 space-y-2">
          <TaskPickerTab objectId={objectId} selectedIds={selectedTaskIds} onChange={setSelectedTaskIds} />
          {touched && selectedTaskIds.length === 0 && (
            <p className="text-[11px] text-red-600">{t('offers:positions.taskList.noneSelected')}</p>
          )}
        </div>
      </TabsContent>
      <TabsContent value="newTask">
        <div className="pt-4">
          {object ? (
            <TaskForm
              objects={[object]}
              services={services}
              onCreated={async (task) => {
                taskJustCreated.current = true;
                await goToBilling([{ taskId: task.taskId, name: task.name, serviceId: task.serviceId }]);
              }}
              onClose={() => {
                if (taskJustCreated.current) {
                  taskJustCreated.current = false;
                  return;
                }
                setTab('tasks');
              }}
            />
          ) : null}
        </div>
      </TabsContent>
    </Tabs>
  );

  const footer = isEdit ? (
    <SlideOverFooter onCancel={onClose} onConfirm={saveEdit} confirmText={t('common:actions.save')} loading={saving} />
  ) : step === 'billing' ? (
    <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 w-full">
      <Button
        variant="secondary"
        onClick={() => setStep('choose')}
        leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={2} />}
      >
        {t('common:actions.back')}
      </Button>
      <Button onClick={saveDrafts} disabled={saving}>
        {t('offers:positions.createPositions', { count: drafts.length })}
      </Button>
    </div>
  ) : objectId && tab === 'tasks' ? (
    <SlideOverFooter
      onCancel={onClose}
      onConfirm={() => {
        setTouched(true);
        if (selectedTaskIds.length === 0) return;
        void goToBilling(tasks.filter((task) => selectedTaskIds.includes(task.taskId)));
      }}
      confirmText={t('offers:positions.toBilling')}
      loading={preparing}
    />
  ) : undefined;

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t('offers:positions.edit') : t('offers:positions.add')}
      size="lg"
      footer={footer}
    >
      {isEdit ? editBody : step === 'billing' ? billingBody : chooseBody}
    </SlideOver>
  );
}
